import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import {
  ClipboardList,
  Calendar,
  MapPin,
  User,
  Search
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const TaskStatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Pending' },
    assigned: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Assigned' },
    in_progress: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'In Progress' },
    completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Completed' },
    cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled' }
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const priorityConfig = {
    low: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Low' },
    medium: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Medium' },
    high: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'High' },
    urgent: { bg: 'bg-red-100', text: 'text-red-700', label: 'Urgent' }
  };

  const config = priorityConfig[priority] || priorityConfig.medium;

  return (
    <span className={`px-2 py-1 rounded text-xs font-semibold ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

export const TechnicianTasks = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 20,
    total: 0,
  });

  useEffect(() => {
    fetchTasks(1);
  }, []);

  const fetchTasks = async (page = 1) => {
    try {
      setLoading(true);

      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API}/work-orders?page=${page}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const result = await response.data; 

      setTasks(result.data);

      setPagination({
        currentPage: result.current_page,
        lastPage: result.last_page,
        perPage: result.per_page,
        total: result.total,
      });
    } 
    catch (error) {
      toast.error('Failed to load tasks', { id: 'tasks-load-error' });
    } 
    finally {
      setLoading(false);
    }
  };

  const goToPage = (page) => {
    if (page < 1 || page > pagination.lastPage)
      return;

    fetchTasks(page);
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      const token = localStorage.getItem('token');

      await axios.put(
        `${API}/work-orders/${taskId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Task status updated!');
      fetchTasks(pagination.currentPage);
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error('Failed to update task status');
    }
  };

  const filteredTasks = tasks.filter(task =>
    task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    task.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    task.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Work Orders & Tasks</h1>
          <p className="text-gray-600 mt-1">Manage your field assignments</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Task List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center border border-gray-200">
            <ClipboardList className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks found</h3>
            <p className="text-gray-600">There are no tasks matching your criteria.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredTasks.map((task) => (
              <div
                key={task.id}
                className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{task.title}</h3>
                      <PriorityBadge priority={task.priority} />
                      <TaskStatusBadge status={task.status} />
                    </div>
                    <p className="text-gray-600 text-sm">{task.description}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  {task.customer_name && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <User className="h-4 w-4" />
                      <span>{task.customer_name}</span>
                    </div>
                  )}
                  
                  {task.scheduled_date && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(task.scheduled_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  
                  {task.location_address && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span className="truncate">{task.location_address}</span>
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                {task.assigned_to === user.id && (
                  <div className="flex gap-2 mt-4 pt-4 border-t border-gray-200">
                    {task.status === 'assigned' && (
                      <button
                        onClick={() => updateTaskStatus(task.id, 'in_progress')}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                      >
                        Start Task
                      </button>
                    )}
                    {task.status === 'in_progress' && (
                      <button
                        onClick={() => updateTaskStatus(task.id, 'completed')}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                      >
                        Complete Task
                      </button>
                    )}
                    {task.meter_serial && (
                      <button
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                        onClick={() => window.location.href = `/meter-reading?task=${task.id}&meter=${task.device_id}`}
                      >
                        Add Reading
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}


        {/* Pagination */}
        {!loading && pagination.lastPage > 1 && (
          <div className="flex justify-between items-center mt-6">
            <div className="texrt-sm text-gray-600">
              Showing {(pagination.currentPage - 1) * pagination.perPage + 1}
              {' to '}
              {Math.min(pagination.currentPage * pagination.perPage, pagination.total)}
              {' of '}
              {pagination.total} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => goToPage(1)}
                disabled={pagination.currentPage === 1}
                className={`px-3 py-1 rounded border ${pagination.currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                First
              </button>

              <button
                onClick={() => goToPage(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className={`px-3 py-1 rounded border ${pagination.currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                Previous
              </button>

              {[...Array(pagination.lastPage).keys()].slice(
                Math.max(0, pagination.currentPage - 3),
                Math.min(pagination.lastPage, pagination.currentPage + 2)
              ).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => goToPage(pageNum + 1)}
                  className={`px-3 py-1 rounded border ${pagination.currentPage === pageNum + 1 ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  {pageNum + 1}
                </button>
              ))}

              <button
                onClick={() => goToPage(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.lastPage}
                className={`px-3 py-1 rounded border ${pagination.currentPage === pagination.lastPage ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                Next
              </button>

              <button
                onClick={() => goToPage(pagination.lastPage)}
                disabled={pagination.currentPage === pagination.lastPage}
                className={`px-3 py-1 rounded border ${pagination.currentPage === pagination.lastPage ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};